import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild, inject } from '@angular/core';
import { IReportCardGraph } from '../interfaces/ireport-card';
import { Chart, registerables } from 'chart.js';
import { GraphType } from '../enums/report-graph';
import { Router } from '@angular/router';
import { AuthService } from '@core/services/auth.service';
import { IUser } from '@core/interfaces/iuser';
import { Role } from '@core/enums/roles';
import { Subject } from 'rxjs';

Chart.register(...registerables);

@Component({
  selector: 'prism-report-card',
  templateUrl: './report-card.component.html',
  styleUrl: './report-card.component.scss'
})
export class ReportCardComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('line') private line?: ElementRef;
  @ViewChild('doughnut') private doughnutCanvas?: ElementRef;
  
  private router = inject(Router);
  private authService = inject(AuthService);
  private destroy$ = new Subject<void>();
  
  lineChart: Chart | null = null;
  
  @Input() title: string = '';
  @Input() data: any[] = [];
  @Input() isCompleted: boolean = false;
  @Input() graph: string = GraphType.line;
  @Input() reportCardData: IReportCardGraph = {};
  @Input() isLoading: boolean = false;
  
  graphType = GraphType;
  private chartInitialized = false;
  user: IUser | null = null;

  constructor() {
    try {
      this.user = this.authService.getCurrentUser();
    } catch (error) {
      console.error('Error getting current user in report card:', error);
      this.user = null;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    try {
      // Redraw on every data change, including an empty result, so a filter
      // that returns nothing does not leave the previous chart on screen.
      if (changes['reportCardData'] && this.chartInitialized) {
        this.updateChart();
      }
    } catch (error) {
      console.error('Error in ngOnChanges:', error);
    }
  }

  ngAfterViewInit(): void {
    try {
      this.chartInitialized = true;
      if (this.reportCardData?.data && this.reportCardData.data.length > 0) {
        this.initChart();
      }
    } catch (error) {
      console.error('Error in ngAfterViewInit:', error);
    }
  }

  ngOnDestroy(): void {
    // clean up chart resources
    if (this.lineChart) {
      this.lineChart.destroy();
      this.lineChart = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
    this.user = null;
    this.reportCardData = {};
  }
  
  updateChart(): void {
    try {
      if (this.lineChart) {
        this.lineChart.destroy();
        this.lineChart = null;
      }
      this.initChart();
    } catch (error) {
      console.error('Error updating chart:', error);
    }
  }

  initChart(): void {
    try {
      switch (this.graph) {
        case GraphType.line:
          this.createLineChart();
          break;
        case GraphType.doughnut:
          this.createDoughnutChart();
          break;
      }
    } catch (error) {
      console.error('Error initializing chart:', error);
    }
  }

  viewDistrictReport(): void {
    try {
      if (!this.user) return;
      
      if (this.user.roleId === Role.DistrictAdmin) {
        const currentDistrict = this.authService.getCurrentDistrict();
        this.router.navigate([`/school-management/${currentDistrict?.id || 0}`]);
      } else {
        this.router.navigate(['/district-management']);
      }
    } catch (error) {
      console.error('Error navigating to district report:', error);
    }
  }

  viewReport(): void {
    try {
      if (!this.reportCardData?.type) return;
      
      if (this.reportCardData.type === 'users') {
        this.router.navigate(['/user-management']);
      } else {
        this.router.navigate(['/report/report-completion-view']);
      }
    } catch (error) {
      console.error('Error navigating to report:', error);
    }
  }

  createLineChart() {
    if (!this.line) {
      console.warn('Line chart canvas not available');
      return;
    }
    
    try {
      this.lineChart = new Chart(this.line.nativeElement, {
        type: 'line',
        data: {
          labels: this.reportCardData.labels || [],
          datasets: [
            {
              label: 'Monthly Data',
              data: this.reportCardData.data || [],
              backgroundColor: 'rgba(0,123,255,0.2)',
              borderColor: 'rgba(0,123,255,1)',
              borderWidth: 2,
              pointBackgroundColor: 'rgba(0,123,255,1)',
              pointBorderColor: '#fff',
              pointHoverBackgroundColor: '#fff',
              pointHoverBorderColor: 'rgba(0,123,255,1)',
              fill: true,
              tension: 0.1
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              min: 0,
              ticks: {
                precision: 0   // whole numbers; let Chart.js pick the step so small districts are readable
              },
              title: {
                display: true
              }
            },
            x: {
              title: {
                display: true
              }
            }
          },
          plugins: {
            legend: {
              display: false,
            }
          }
        }
      });
    } catch (error) {
      console.error('Error creating line chart:', error);
    }
  }

  createDoughnutChart() {
    if (!this.doughnutCanvas) {
      console.warn('Doughnut chart canvas not available');
      return;
    }
    
    try {
      this.lineChart = new Chart(this.doughnutCanvas.nativeElement, {
        type: 'doughnut',
        data: {
          labels: this.reportCardData.labels || [],
          datasets: [
            {
              data: this.reportCardData.data || [],
              backgroundColor: [
                'rgba(122, 133, 255, 0.8)', 
                'rgba(231, 150, 255, 0.8)',  
              ],
              borderWidth: 1,
              hoverOffset: 4
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '70%',
          plugins: {
            legend: {
              display: true,
              position: 'bottom',
              labels: {
                padding: 20,
                boxWidth: 12
              }
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  const label = context.label || '';
                  const value = context.formattedValue;
                  return `${label}: ${value}%`;
                }
              }
            }
          },
          scales: {}
        }
      });
    } catch (error) {
      console.error('Error creating doughnut chart:', error);
    }
  }
}